import copy from "@/services/copy";
import _ from "lodash";

function defaultPagination(itemsPerPage, sort) {
    return {
        page: 1,
        itemsPerPage,
        total: 0,
        sort,
    };
}

function defaultData() {
    return [];
}

function defaultExpended() {
    return [];
}

function generateSearchGetters(searchParameters) {
    const searchGetters = {};
    const keys = Object.keys(searchParameters || {});
    for (const k of keys) {
        const name = searchField(k);
        searchGetters[name] = state => state.search[k];
    }
    return searchGetters;
}

function generateStateActions() {
    const FETCH_PAGE = "fetchPage"
    return {
        updateSearchParameters({ state, commit, dispatch }, { field, value }) {
            const oldValue = state[field];
            if (oldValue !== value) {
                commit("searchParameters", { field, value });
                dispatch("debouncedFetchPage");
            }
        },
        patchItem({ state, commit }, { id, patcher }) {
            const index = state.data.findIndex(item => item.id === id);
            if (index >= 0) {
                commit("patchItem", { index, patcher });
                return state.data[index];
            }
            return null;
        },
        updateItemsPerPage({ state, commit, dispatch }, itemsPerPage) {
            if (state.pagination.itemsPerPage !== itemsPerPage) {
                commit("itemsPerPage", itemsPerPage);
                dispatch(FETCH_PAGE);
            }
        },
        unexpand({ commit, state }, item) {
            const filtered = state.expanded.filter(e => e.id !== item.id);
            commit("expanded", filtered);
        },
        expanded({ commit }, expanded) {
            commit("expanded", expanded);
        },
        updatePage({ state, commit, dispatch }, page) {
            if (state.pagination.page !== page) {
                commit("page", page);
                dispatch(FETCH_PAGE);
            }
        },
        updateSort({ state, commit, dispatch }, sort) {
            if (state.pagination.sort !== sort) {
                commit("sort", sort);
                dispatch(FETCH_PAGE);
            }
        },
        firstFetch({ state, dispatch }) {
            if (state.nextPage === 0) {
                dispatch(FETCH_PAGE);
            }
        },
        processFetchPage({ state, commit }, { input, expected }) {
            if (state.nextPage === expected) {
                commit("setResults", input);
            }
            return null;
        },
        reload({ state, commit, dispatch }) {
            const pagination = copy(state.pagination);
            commit("setResults", {
                data: defaultData(),
                pagination
            });
            dispatch(FETCH_PAGE);
        },
        clear({ state, commit }) {
            const pagination = copy(state.pagination);
            commit("setResults", {
                data: defaultData(),
                pagination: {
                    sort: pagination.sort,
                    page: 1,
                    itemsPerPage: pagination.itemsPerPage,
                    total: 0,
                },
            });
        }
    }
}

const SEARCH_SEPARATOR = "__";
export function searchField(name) {
    return `${SEARCH_SEPARATOR}${name}`
}

export function makePaginatedTable(service, searchParameters, options = {}) {

    const unpaged = !!options.unpaged;

    const abortHolder = {
        abort: null,
    }

    function fetchPage({ state, dispatch, commit }) {
        if (state.disableFetch) {
            return Promise.resolve();
        }

        if (abortHolder.abort) {
            abortHolder.abort.abort();
            abortHolder.abort = null;
        }

        abortHolder.abort = new AbortController();

        commit("loading", 1);
        const pagination = copy(state.pagination);
        const expected = copy(state.nextPage + 1);
        commit("nextPage", expected);

        var page = pagination.page;
        var itemsPerPage = pagination.itemsPerPage;
        if (unpaged) {
            page = 0;
            itemsPerPage = -1;
        }

        return service(copy(state.search), {
            sort: pagination.sort,
            page,
            itemsPerPage,
        }, abortHolder.abort.signal)
            .then(input => {
                return dispatch("processFetchPage", { input, expected });
            }).catch(() => {
                //noop
             })
            .finally(() => {
                abortHolder.abort = null;
                commit("loading", -1);
            });
    }

    const debouncedFetchPage = _.debounce(fetchPage, options.debounce || 250);

    return {
        namespaced: true,
        state: {
            unpaged,
            search: searchParameters,
            pagination: defaultPagination(options.itemsPerPage || 20, options.sort || []),
            data: defaultData(),
            expanded: defaultExpended(),
            nextPage: 0,
            loading: 0,
            disableFetch: false,
        },
        getters: {
            ...generateSearchGetters(searchParameters),
            unpaged(state) {
                return state.unpaged;
            },
            loading(state) {
                return state.loading > 0;
            },
            pagination(state) {
                return state.pagination;
            },
            data(state) {
                return state.data;
            },
            expanded(state) {
                return state.expanded;
            },
            disableFetch(state) {
                return state.disableFetch;
            }
        },
        mutations: {
            disableFetch(state, disableFetch) {
                state.disableFetch = disableFetch;
            },
            searchParameters(state, { field, value }) {
                state.pagination.page = 0;
                state.search[field] = value;
            },
            nextPage(state, nextPage) {
                state.nextPage = nextPage;
            },
            itemsPerPage(state, itemsPerPage) {
                state.pagination.itemsPerPage = itemsPerPage;
            },
            sort(state, sort) {
                state.pagination.sort = sort;
            },
            page(state, page) {
                state.pagination.page = page;
            },
            expanded(state, expanded) {
                state.expanded = expanded;
            },
            loading(state, delta) {
                state.loading += delta;
            },
            setResults(state, { data, pagination }) {
                state.data = data;
                state.pagination = { ...pagination, sort: state.pagination.sort };
                state.expanded = defaultExpended();
            },
            patchItem(state, { index, patcher }) {
                const data = [...state.data];
                data[index] = patcher(data[index]);
                state.data = data;
            },
        },
        actions: {
            ...generateStateActions(),
            fetchPage,
            debouncedFetchPage,
        }
    };
}
